import { Fragment, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";

import { Login } from "./features/auth/Login";
import { SetPassword } from "./features/auth/SetPassword";
import { InstallBanner } from "./features/shell/InstallBanner";
import { NotificationNudge } from "./features/shell/NotificationNudge";
import { RestoreDroppedPush } from "./features/shell/RestoreDroppedPush";
import { StormPage } from "./features/storm/StormPage";
import { CountyStormPage } from "./features/storm/CountyStormPage";
import { IntakePage } from "./features/intake/IntakePage";
import { ColorsPage } from "./features/present/ColorsPage";
import { CrewPacketPage } from "./features/crewPacket/CrewPacketPage";
import { ChangeOrderPage } from "./features/present/ChangeOrderPage";
import { CertificatePage } from "./features/present/CertificatePage";
import { PresentPage } from "./features/present/PresentPage";
import { Shell } from "./features/shell/Shell";
import { GoogleReturn } from "./features/tools/GoogleReturn";
import { UpdateBanner } from "./features/shell/UpdateBanner";
import { language, onLanguageChange } from "./i18n";
import { authLink, requiresNewPassword } from "./lib/authLink";
import { GOOGLE_RETURN_PATH } from "./lib/gmail";
import { initServiceWorker } from "./lib/pwa";
import { supabase } from "./lib/supabase";
import {
  hasPublicLink,
  publicLinksFrom,
  type PublicLinks,
} from "./publicRoutes";
import { SessionProvider } from "./session";

/**
 * The page for a link that belongs to somebody who is not staff, or null.
 *
 * Every one of these is answered before Auth is touched; see the comments on
 * each in `App` for why. Kept out of `App` so the list can grow without
 * `App` growing with it.
 */
function publicPageFor(links: PublicLinks) {
  if (links.intakeSlug !== null) return <IntakePage slug={links.intakeSlug} />;
  if (links.colorSlug !== null) return <ColorsPage slug={links.colorSlug} />;
  if (links.crewPacketToken)
    return <CrewPacketPage token={links.crewPacketToken} />;
  if (links.presentToken) return <PresentPage token={links.presentToken} />;
  if (links.changeOrderToken)
    return <ChangeOrderPage token={links.changeOrderToken} />;
  if (links.certificateToken)
    return <CertificatePage token={links.certificateToken} />;
  if (links.stormToken) return <StormPage token={links.stormToken} />;
  if (links.countyReport)
    return (
      <CountyStormPage
        county={links.countyReport.county}
        date={links.countyReport.date}
      />
    );
  return null;
}

export function App() {
  /**
   * A link that belongs to somebody who is not staff is answered before
   * anything else happens, and must not touch Auth: restoring a session,
   * showing the install banner or asking about notifications would all be
   * wrong on a stranger's phone. Read once, from the path the page loaded
   * with; this decision cannot change while the page is open.
   *
   * - `/p/<token>`: the customer's proposal and contract.
   * - `/c/<token>`: a change order, for work found after the contract was
   *   signed.
   * - `/cert/<token>`: the customer signing a certificate of completion.
   * - `/s/<token>`: an adjuster reading a storm history report.
   * - `/storms/<county>/<date>`: the most public of these; a partner forwards
   *   it to clients who have never heard of us, so it carries no token.
   * - `/go/<slug>`: the intake link a rep texts a stranger. The empty string
   *   is a real value and means the link with no rep on it.
   * - `/colors` and `/colors/<slug>`: the colour options a rep sends on their
   *   own (Jonathon, 2026-09-30). The empty string is the list of every page.
   * - `/sub/<token>`: a subcontractor's packet: their W-9, bank account,
   *   insurance and the agreement, signed on their own phone (ADR 0279).
   *
   * The list is `publicRoutes.ts`, which main.tsx reads too, to keep every one
   * of these pages in English (ADR 0325).
   */
  const [links] = useState(() => publicLinksFrom(window.location.pathname));
  const isPublic = hasPublicLink(links);

  /**
   * Coming back from Google, which is staff and needs the session restored —
   * so unlike the customer's link this is read alongside the sign-in, not
   * instead of it. Read once, for the same reason: the code in the query string
   * is spent on the first exchange.
   */
  const [returningFromGoogle] = useState(
    () => window.location.pathname === GOOGLE_RETURN_PATH,
  );

  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  // Set when an invite or reset link brought us here, cleared once the new
  // password is saved. Until then the app is not shown: an invited user has no
  // password yet, and letting them past would strand them at the next sign-in.
  const [newPasswordFor, setNewPasswordFor] = useState<
    "invite" | "recovery" | null
  >(null);

  /**
   * The language, as a key on the screens staff see rather than on App.
   *
   * `t()` reads the language from the i18n module, so nothing re-renders on
   * its own when the button is pressed; keying the screens on it draws them
   * again in the other language (ADR 0325). The key sits below this
   * component's startup work on purpose: the invite or reset link is
   * installed, and the service worker registered, once per page load, and
   * redrawing the screens must not do either again.
   */
  const [lang, setLang] = useState(language);
  useEffect(() => onLanguageChange(setLang), []);

  useEffect(() => {
    if (isPublic) return;

    async function restore() {
      // The client runs with detectSessionInUrl:false, so the session that came
      // back in the email link's URL fragment is ours to install. See
      // lib/authLink.ts.
      if (
        requiresNewPassword(authLink) &&
        authLink.accessToken &&
        authLink.refreshToken
      ) {
        const { error } = await supabase.auth.setSession({
          access_token: authLink.accessToken,
          refresh_token: authLink.refreshToken,
        });
        // A failed exchange means an expired link; Login reports it.
        if (!error && authLink.type !== "other")
          setNewPasswordFor(authLink.type);
      }

      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      setChecking(false);
    }

    void restore();

    const { data } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next);
      // Covers a reset link that arrives while the app is already open.
      if (event === "PASSWORD_RECOVERY") setNewPasswordFor("recovery");
    });
    return () => data.subscription.unsubscribe();
  }, [isPublic]);

  useEffect(() => {
    if (isPublic) return;
    initServiceWorker(setUpdateAvailable);
  }, [isPublic]);

  const publicPage = publicPageFor(links);
  if (publicPage) return publicPage;

  if (checking) return null;

  return (
    <Fragment key={lang}>
      {updateAvailable && <UpdateBanner />}
      {session && newPasswordFor ? (
        <SetPassword
          reason={newPasswordFor}
          onDone={() => setNewPasswordFor(null)}
        />
      ) : session ? (
        <SessionProvider session={session}>
          <RestoreDroppedPush />
          {/* One bar at a time, and a waiting build outranks a nudge. The two
              nudges cannot collide: one only shows before installing, the
              other only after. */}
          {!updateAvailable && !returningFromGoogle && (
            <>
              <InstallBanner />
              <NotificationNudge />
            </>
          )}
          {returningFromGoogle ? <GoogleReturn /> : <Shell />}
        </SessionProvider>
      ) : (
        <Login />
      )}
    </Fragment>
  );
}
